import {
  Outlet,
  type RouterHistory,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router';

import { AccountsPage } from '../pages/AccountsPage';
import { NotificationsPage } from '../pages/NotificationsPage';
import { NotificationTemplatesPage } from '../pages/NotificationTemplatesPage';
import { BannersPage } from '../pages/BannersPage';
import { BroadcastOverlayPage } from '../pages/BroadcastOverlayPage';
import { DashboardPage } from '../pages/DashboardPage';
import { DlqPage } from '../pages/DlqPage';
import { DrawsPage } from '../pages/DrawsPage';
import { ExportPage } from '../pages/ExportPage';
import { LoginPage } from '../pages/LoginPage';
import { MODERATION_TABS, ModerationPage, type ModerationTab } from '../pages/ModerationPage';
import { PhotoDetailPage } from '../pages/PhotoDetailPage';
import { ReceiptDetailPage } from '../pages/ReceiptDetailPage';
import { TicketDetailPage } from '../pages/TicketDetailPage';
import { UserDetailPage } from '../pages/UserDetailPage';
import { RevealConsolePage } from '../pages/RevealConsolePage';
import { RewardRedemptionsPage } from '../pages/RewardRedemptionsPage';
import { RewardsPage } from '../pages/RewardsPage';
import { UsersPage } from '../pages/UsersPage';
import { AdminShell } from './AdminShell';
import { isAuthenticated } from '../lib/auth-store';

const rootRoute = createRootRoute({ component: () => <Outlet /> });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  beforeLoad: () => {
    // Already signed in? Skip the form.
    if (isAuthenticated()) throw redirect({ to: '/' });
  },
});

/** Public OBS Browser Source. Its draw-scoped credential lives in the URL fragment. */
const broadcastOverlayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/overlay/$drawId',
  component: BroadcastOverlayPage,
});

/**
 * Pathless layout route: everything under it is behind the admin session. The guard lives here
 * rather than on each page so a new page cannot be added unprotected by accident.
 */
const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'shell',
  component: AdminShell,
  beforeLoad: ({ location }) => {
    if (!isAuthenticated()) {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
  },
});

/**
 * Bosh sahifa — boshqaruv paneli.
 *
 * Ilgari bu yerda tortishuvlar jadvali turardi va u yagona ekran bo'lgan paytda to'g'ri edi.
 * Endi panel oltita bo'limdan iborat: kunni "hozir qayerda ish bor" degan javob bilan
 * boshlash, keyin kerakli bo'limga o'tish — jadvalning o'zi esa `/draws` da.
 */
const dashboardRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/',
  component: DashboardPage,
});

const drawsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/draws',
  component: DrawsPage,
});

/** Efir konsoli — bitta tortishuvning ichi. */
const revealConsoleRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/draws/$drawId',
  component: RevealConsolePage,
});

/**
 * Moderatsiya — faol tab va sahifa raqami URL da.
 *
 * Ular komponent holatida yashaganda detail sahifaga o'tib qaytish operatorni har safar
 * birinchi tabning birinchi sahifasiga tashlardi, ya'ni detail sahifalarni qo'shish ro'yxatni
 * yurishni buzardi. Noto'g'ri qiymat jimgina standartga tushadi: qo'lda tuzatilgan manzil
 * ekranni yiqitmasligi kerak.
 */
const moderationRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/moderation',
  component: ModerationPage,
  validateSearch: (search: Record<string, unknown>): { tab: ModerationTab; page: number } => {
    const tab = MODERATION_TABS.find((value) => value === search.tab) ?? MODERATION_TABS[0];
    const page = Number(search.page);
    return { tab, page: Number.isInteger(page) && page > 0 ? page : 1 };
  },
});

/**
 * Moderatsiya detail sahifalari.
 *
 * Ular `/moderation` ning BOLASI emas, qo'shnisi: ichma-ich marshrut ro'yxatni ham chizishga
 * majbur qilardi (uchta jadval + filtrlar), holbuki detail ochilganda ro'yxat kerak emas —
 * unga qaytish uchun sahifadagi "Orqaga" havolasi bor.
 */
const receiptDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/moderation/receipts/$receiptId',
  component: ReceiptDetailPage,
});

const photoDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/moderation/photos/$photoScanId',
  component: PhotoDetailPage,
});

const ticketDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/moderation/tickets/$ticketId',
  component: TicketDetailPage,
});

const dlqRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/dlq',
  component: DlqPage,
});

const usersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/users',
  component: UsersPage,
});

/** Kartochka — o'z manzilida: u ulashiladi va unga "orqaga" bilan qaytiladi. */
const userDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/users/$userId',
  component: UserDetailPage,
});

const exportRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/export',
  component: ExportPage,
});

/** Admin hisoblari — rol guardi ostidagi yuza, ya'ni operatorga bo'sh ro'yxat ko'rinadi. */
const accountsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/accounts',
  component: AccountsPage,
});

/** Ball-katalog — foydalanuvchilar ball evaziga oladigan sovg'alar ro'yxati (P8). */
const rewardsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/rewards',
  component: RewardsPage,
});

/**
 * Sovg'a buyurtmalari — ball evaziga olingan sovg'alarni topshirish navbati.
 *
 * `/rewards` (katalog) dan alohida marshrut: katalog kuniga bir marta tahrirlanadi,
 * buyurtmalar esa kundalik ish — ularni bitta sahifada tab qilib qo'yish navbatni katalog
 * boshqaruvi ortiga yashirardi.
 */
const rewardRedemptionsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/reward-redemptions',
  component: RewardRedemptionsPage,
});

/** Xabarlar navbati — yetkazib berish monitoringi. */
const notificationsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/notifications',
  component: NotificationsPage,
});

/** Bot xabarlari — Telegramga ketadigan shablonlar. */
const notificationTemplatesRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/notification-templates',
  component: NotificationTemplatesPage,
});

/** Bannerlar — bosh ekrandagi banner-slayder boshqaruvi. */
const bannersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/banners',
  component: BannersPage,
});

export const routeTree = rootRoute.addChildren([
  loginRoute,
  broadcastOverlayRoute,
  shellRoute.addChildren([
    dashboardRoute,
    drawsRoute,
    revealConsoleRoute,
    moderationRoute,
    receiptDetailRoute,
    photoDetailRoute,
    ticketDetailRoute,
    dlqRoute,
    usersRoute,
    userDetailRoute,
    exportRoute,
    accountsRoute,
    rewardsRoute,
    rewardRedemptionsRoute,
    bannersRoute,
    notificationTemplatesRoute,
    notificationsRoute,
  ]),
]);

/** Tests build their own router over a memory history; the app uses the default browser one. */
export function createAdminRouter(history?: RouterHistory) {
  return createRouter({ routeTree, history });
}

export const router = createAdminRouter();

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
